import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { wrapHandlers } from '@reckon-web/utils';
import {
  InputSizeType,
  InputWeightType,
  useInputStyles,
} from '@reckon-web/text-input';
import { usePack, useTheme } from '@reckon-web/theme';

export const ContentEditable: FC<{
  value: string | null | undefined;
  onChange: (value: string) => void;
  onBlur: () => void;
  onFocus: () => void;
  invalid: boolean;
  'aria-describedby'?: string;
  id: string;
  labelId: string;
  placeholder: string;
  weight?: InputWeightType;
  size?: InputSizeType;
  rows?: number;
  testId?: string;
}> = ({
  value,
  onChange,
  onBlur,
  onFocus,
  invalid,
  'aria-describedby': ariaDescribedby,
  id,
  labelId,
  placeholder,
  weight = 'subtle',
  size = 'medium',
  rows = 8,
  testId = `content-editable`,
  ...rest
}) => {
  const theme = useTheme();
  const sizes = usePack('sizes');
  const domNode = useRef<HTMLDivElement | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const handleFocus = wrapHandlers(onFocus, () => {
    // No special handling needed for focus
  });

  const handleBlur = wrapHandlers(onBlur, () => {
    // No special handling needed for blur
  });

  const handleInput = useCallback(() => {
    if (domNode.current) {
      // Get the plain text content and pass it up
      const textContent = domNode.current.innerText || '';
      onChange(textContent);
    }
  }, [onChange]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();

    if (!e.clipboardData) {
      return;
    }

    // Get plain text only to avoid HTML injection
    const text = e.clipboardData.getData('text/plain');

    if (text && domNode.current) {
      // Insert plain text at cursor position
      document.execCommand('insertText', false, text);
    }
  }, []);

  // Initialize content only once when the component mounts
  useEffect(() => {
    if (domNode.current && !isInitialized) {
      domNode.current.textContent = value || '';
      setIsInitialized(true);
    }
  }, [value, isInitialized]);

  // Only update content when value changes externally and we don't have focus
  useEffect(() => {
    if (
      domNode.current &&
      isInitialized &&
      document.activeElement !== domNode.current
    ) {
      const currentText = domNode.current.textContent || '';
      const newValue = value || '';

      // Only update if the content is actually different
      if (currentText !== newValue) {
        domNode.current.textContent = newValue;
      }
    }
  }, [value, isInitialized]);

  const styles = useInputStyles({
    size: size,
    shape: 'square',
    weight,
  });

  const minHeight = `calc(${theme.typography.leading.base} * ${sizes[size].fontSizeText} * 4)`;
  const maxHeight =
    rows > 0
      ? `calc(${theme.typography.leading.base} * ${sizes[size].fontSizeText} * ${rows})`
      : undefined;

  return (
    <div
      contentEditable={true}
      suppressContentEditableWarning={true}
      aria-invalid={invalid}
      aria-describedby={ariaDescribedby}
      aria-labelledby={labelId}
      aria-placeholder={placeholder}
      role="textbox"
      id={id}
      ref={domNode}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onPaste={handlePaste}
      onInput={handleInput}
      css={{
        ...styles,
        lineHeight: theme.typography.leading.base,
        paddingTop: sizes[size].paddingY,
        paddingBottom: sizes[size].paddingY,
        overflowWrap: 'break-word',
        wordWrap: 'break-word',
        wordBreak: 'break-word',
        hyphens: 'auto',
        minHeight,
        maxHeight,
        height: 'auto',
        overflowY: 'auto',
        position: 'relative',
        whiteSpace: 'pre-wrap', // Preserve line breaks
        '&:empty:before': {
          content: 'attr(aria-placeholder)',
          pointerEvents: 'none',
          color: theme.palette.formInput.textPlaceholder,
          position: 'absolute',
        },
      }}
      data-testid={testId}
      {...rest}
    />
  );
};
